import React, { useEffect, useRef, useState } from "react";
import { BRAND } from "./helpers.js";

const FONT = 'JybekSig, "Segoe Script", "Brush Script MT", "Lucida Handwriting", "Apple Chancery", cursive';

function ensureFont() {
  if (document.getElementById("jybek-sig-font")) return;
  const st = document.createElement("style");
  st.id = "jybek-sig-font";
  st.textContent = `@font-face { font-family: JybekSig; src: url(/fonts/jybek-sig.woff2) format("woff2"); font-display: swap; }`;
  document.head.appendChild(st);
}

/**
 * Adopt a signature + initials: typed (rendered in a script style) or drawn with a finger / mouse.
 * onAdopt({ name, initials, title?, method, sig, init }) must return a promise.
 */
export default function SignatureAdopt({ defaultName = "", defaultInitials = "", withTitle = false, defaultTitle = "Human Resources", legal, onAdopt, submitLabel = "Adopt and continue" }) {
  const sigRef = useRef(null);
  const iniRef = useRef(null);
  const [name, setName] = useState(defaultName);
  const [initials, setInitials] = useState(defaultInitials || defaultName.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 3));
  const [title, setTitle] = useState(defaultTitle);
  const [mode, setMode] = useState("typed");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const drawn = useRef({ sig: false, ini: false });

  const drawTyped = () => {
    [[sigRef.current, name, 54], [iniRef.current, initials.toUpperCase(), 64]].forEach(([c, text, size]) => {
      if (!c) return;
      const x = c.getContext("2d");
      x.clearRect(0, 0, c.width, c.height);
      x.fillStyle = "#12245a"; x.textBaseline = "middle"; x.textAlign = "center";
      let s = size; x.font = `${s}px ${FONT}`;
      while (x.measureText(text).width > c.width - 20 && s > 18) { s -= 2; x.font = `${s}px ${FONT}`; }
      x.fillText(text, c.width / 2, c.height / 2 + 4);
    });
  };

  useEffect(() => {
    ensureFont();
    if (document.fonts) document.fonts.load(`48px JybekSig`).then(() => mode === "typed" && drawTyped()).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mode === "typed") drawTyped(); }); // redraw on every change while typing

  const clearPads = () => {
    [sigRef.current, iniRef.current].forEach((c) => c && c.getContext("2d").clearRect(0, 0, c.width, c.height));
    drawn.current = { sig: false, ini: false };
  };

  const stroke = useRef({ down: false, last: null });
  const padHandlers = (ref, key) => {
    const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); return [(e.clientX - r.left) * c.width / r.width, (e.clientY - r.top) * c.height / r.height]; };
    const stop = () => { stroke.current.down = false; };
    return {
      onPointerDown: (e) => { if (mode !== "drawn") return; stroke.current = { down: true, last: pos(e) }; e.currentTarget.setPointerCapture(e.pointerId); },
      onPointerMove: (e) => {
        if (!stroke.current.down) return;
        const x = ref.current.getContext("2d"); const p = pos(e); const last = stroke.current.last;
        x.strokeStyle = "#12245a"; x.lineWidth = 3.2; x.lineCap = "round"; x.lineJoin = "round";
        x.beginPath(); x.moveTo(last[0], last[1]); x.lineTo(p[0], p[1]); x.stroke(); stroke.current.last = p; drawn.current[key] = true;
      },
      onPointerUp: stop, onPointerCancel: stop, onPointerLeave: stop,
    };
  };

  const submit = async () => {
    setError("");
    if (name.trim().length < 3) return setError("Enter your full legal name.");
    if (!initials.trim()) return setError("Enter your initials.");
    if (!agree) return setError("Please check the box to agree.");
    if (mode === "drawn" && !(drawn.current.sig && drawn.current.ini)) return setError("Please draw both your signature and your initials.");
    if (mode === "typed") drawTyped();
    setBusy(true);
    try {
      await onAdopt({ name: name.trim(), initials: initials.trim().toUpperCase(), title: withTitle ? title.trim() : undefined, method: mode, sig: sigRef.current.toDataURL("image/png"), init: iniRef.current.toDataURL("image/png") });
    } catch (e) {
      setError(e.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const tab = (m, label) => (
    <button type="button" onClick={() => { setMode(m); if (m === "drawn") clearPads(); }}
      className={`px-4 py-2 text-sm font-semibold rounded-lg ${mode === m ? "text-white" : "text-gray-600 bg-gray-100"}`}
      style={mode === m ? { backgroundColor: BRAND } : {}}>{label}</button>
  );

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="sm:col-span-2 text-sm">Full legal name
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" autoComplete="name" />
        </label>
        <label className="text-sm">Initials
          <input value={initials} maxLength={4} onChange={(e) => setInitials(e.target.value.toUpperCase())} className="mt-1 w-full border rounded-lg px-3 py-2 uppercase" />
        </label>
        {withTitle && (
          <label className="sm:col-span-3 text-sm">Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" />
          </label>
        )}
      </div>
      <div className="flex gap-2">{tab("typed", "Type it")}{tab("drawn", "Draw it")}</div>
      {mode === "drawn" && <p className="text-xs text-gray-500">Use your finger or mouse to sign in each box.</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Signature</div>
          <canvas ref={sigRef} width={600} height={150} {...padHandlers(sigRef, "sig")} className="w-full border-2 border-dashed rounded-lg bg-white touch-none" style={{ aspectRatio: "4 / 1" }} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Initials</div>
          <canvas ref={iniRef} width={240} height={150} {...padHandlers(iniRef, "ini")} className="w-full border-2 border-dashed rounded-lg bg-white touch-none" style={{ aspectRatio: "8 / 5" }} />
        </div>
      </div>
      {mode === "drawn" && <button type="button" onClick={clearPads} className="text-sm underline text-gray-600">Clear</button>}
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <span>{legal || "I agree that this signature and these initials are the electronic representation of my signature and initials for all purposes when I use them on documents, just the same as a pen-and-paper signature or initial."}</span>
      </label>
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
      <button type="button" disabled={busy} onClick={submit} className="w-full sm:w-auto px-6 py-3 rounded-lg text-white font-semibold disabled:opacity-60" style={{ backgroundColor: BRAND }}>
        {busy ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}
